import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { defaultValuesMap, schemaMap } from "../schema/GroupFilterSchema";
import HeaderFilter from "./group-report/HeaderFilter";
import useReportAgentV4 from "../../../services/cm-group-report/hooks/useReportAgentV4";
import useRoomReportV4 from "../../../services/cm-group-report/hooks/useRoomReportV4";
import useDailyReportV4 from "../../../services/cm-group-report/hooks/useDailyReportV4";
import useSalesReportV4 from "../../../services/cm-group-report/hooks/useSalesReportV4"; // <-- 1. Import hook V4 untuk Laporan Jualan
import useCountryReportV4 from "../../../services/cm-group-report/hooks/useCountryReportV4";

import SkeletonTable from "./group-report/SkeletonTable";
import ReportAgentTableV4 from "./group-report/ReportAgentTableV4";
// import GroupAgentEChartV4 from "./group-report/GroupAgentEChartV4";
import RoomReportTableV4 from "./group-report/RoomReportTableV4";
// import GroupRoomEChartV4 from "./group-report/GroupRoomEChartV4";
import DailyReportTableV4 from "./group-report/DailyReportTableV4";
import SalesReportTableV4 from "./group-report/SalesReportTableV4"; // <-- 1. Import tabel V4 untuk Laporan Jualan
import CountryReportTableV4 from "./group-report/CountryReportTableV4";

import { formatPrice } from "../../../shared/utils/formatPrice";
import ReportReservationTableV4 from "./group-report/ReportReservationTableV4";
// import ReportReservationChartV4 from "./group-report/ReportReservationChartV4";
import useReservationReportV4 from "../../../services/cm-group-report/hooks/useReservationReportV4";
import useReportSalesMonthlyV4 from "../../../services/cm-group-report/hooks/useReportSalesMonthlyV4";
import ReportSalesMonthlyTableV4 from "./group-report/ReportSalesMonthlyTableV4";
// import ReportSalesMonthlyChartV4 from "./group-report/ReportSalesMonthlyChartV4";
import GroupAgentHighchartsV4 from "./group-report/GroupAgentHighchartsV4";
import GroupRoomHighchartsV4 from "./group-report/GroupRoomHighchartsV4";
// import ReportReservationHighChart from "./property-report/ReportReservationHighChart";
import ReportReservationHighchartsV4 from "./group-report/ReportReservationHighchartsV4";
import ReportSalesMonthlyHighchartsV4 from "./group-report/ReportSalesMonthlyHighchartsV4";
import useOccupancyMonthlyReportV4 from "../../../services/cm-group-report/hooks/useOccupancyMonthlyReportV4";
import OccupancyReportTableV4 from "./group-report/OccupancyReportTableV4";
import useOccupancyYearlyReportV4 from "../../../services/cm-group-report/hooks/useOccupancyYearlyReportV4";

type ReportType = keyof typeof schemaMap;

const GroupContent: React.FC = () => {
    const [reportType, setReportType] = useState<ReportType>("report_agent");
    const [filters, setFilters] = useState(() => defaultValuesMap["report_agent"]);

    // State pengisihan untuk Laporan Agen
    const [agentOrderBy, setAgentOrderBy] = useState<string>('agent_name');
    const [agentSort, setAgentSort] = useState<'asc' | 'desc'>('desc');

    // State pengisihan untuk Laporan Bilik
    const [roomOrderBy, setRoomOrderBy] = useState<string>('property');
    const [roomSort, setRoomSort] = useState<'asc' | 'desc'>('desc');

    // State pengisihan untuk Laporan Harian
    const [dailyOrderBy, setDailyOrderBy] = useState<string>('date');
    const [dailySort, setDailySort] = useState<'asc' | 'desc'>('asc');

    // <-- 2. State pengisihan terpisah untuk Laporan Jualan
    const [salesOrderBy, setSalesOrderBy] = useState<string>('booking_date');
    const [salesSort, setSalesSort] = useState<'asc' | 'desc'>('asc');

    const [countryOrderBy, setCountryOrderBy] = useState<string>('nb_reservations');
    const [countrySort, setCountrySort] = useState<'asc' | 'desc'>('desc');

    const { data: dailyReportData, isFetching: isDailyReportFetching } = useDailyReportV4({
        filter: filters.filter as any,
        orderBy: dailyOrderBy,
        sort: dailySort,
        type: reportType
    });

    // <-- 3. Gunakan hook V4 untuk Laporan Jualan
    const { data: salesReportData, isFetching: isSalesReportFetching } = useSalesReportV4({
        filter: filters.filter as any,
        orderBy: salesOrderBy,
        sort: salesSort,
        type: reportType
    });

    const { data: reportSalesMonthlyData, isFetching: isReportSalesMonthlyFetching } = useReportSalesMonthlyV4({
        filter: filters.filter as any, type: reportType
    });

    const { data: reportReservationData, isFetching: isReportReservationFetching } = useReservationReportV4({
        filter: filters.filter as any, type: reportType
    });

    const { data: reportAgentData, isFetching: isReportAgentFetching } = useReportAgentV4({
        filter: filters.filter as any,
        orderBy: agentOrderBy,
        sort: agentSort,
        type: reportType
    });

    const { data: roomData, isFetching: isRoomFetching } = useRoomReportV4({
        filter: filters.filter as any,
        orderBy: roomOrderBy,
        sort: roomSort,
        type: reportType
    });

    const { data: countryReportData, isFetching: isCountryReportFetching } = useCountryReportV4({
        filter: filters.filter as any,
        orderBy: countryOrderBy, sort: countrySort, type: reportType
    });

    const { data: occupancyPropertyMonthlyData, isFetching: isOccupancyPropertyMonthlyFetching } = useOccupancyMonthlyReportV4({
        filter: {
            property_ids: filters.filter?.property_id,
            start_date: (filters.filter as any)?.year ? `${(filters.filter as any).year}-01-01` : '2025-01-01',
            end_date: (filters.filter as any)?.year ? `${(filters.filter as any).year}-12-31` : '2025-12-31',
        },
        type: reportType,
        group_by: "property"
    });

    const { data: occupancyPropertyMonthlyDataByMonth, isFetching: isOccupancyPropertyMonthlyByMonthFetching } = useOccupancyMonthlyReportV4({
        filter: {
            property_ids: filters.filter?.property_id,
            start_date: (filters.filter as any)?.year ? `${(filters.filter as any).year}-01-01` : '2025-01-01',
            end_date: (filters.filter as any)?.year ? `${(filters.filter as any).year}-12-31` : '2025-12-31',
        },
        type: reportType,
        group_by: "month"
    });

    const { data: occupancyPropertyYearlyData, isFetching: isOccupancyPropertyYearlyFetching } = useOccupancyYearlyReportV4({
        filter: {
            property_ids: filters.filter?.property_id,
            start_date: (filters.filter as any)?.year ? `${(filters.filter as any).year}-01-01` : '2025-01-01',
            end_date: (filters.filter as any)?.year ? `${(filters.filter as any).year}-12-31` : '2025-12-31',
        },
        type: reportType,
        group_by: "property"
    });

    const { data: occupancyPropertyYearlyDataByYear, isFetching: isOccupancyPropertyYearlyByYearFetching } = useOccupancyYearlyReportV4({
        filter: {
            property_ids: filters.filter?.property_id,
            start_date: (filters.filter as any)?.year ? `${(filters.filter as any).year}-01-01` : '2025-01-01',
            end_date: (filters.filter as any)?.year ? `${(filters.filter as any).year}-12-31` : '2025-12-31',
        },
        type: reportType,
        group_by: "year"
    });

    // Toast notifikasi saat data berhasil di-load sesuai reportType aktif
    useEffect(() => {
        const map: Record<string, { fetching: boolean; data: any }> = {
            daily_report: { fetching: isDailyReportFetching, data: dailyReportData },
            sales_report: { fetching: isSalesReportFetching, data: salesReportData },
            report_sales_monthly: { fetching: isReportSalesMonthlyFetching, data: reportSalesMonthlyData },
            report_reservation: { fetching: isReportReservationFetching, data: reportReservationData },
            report_agent: { fetching: isReportAgentFetching, data: reportAgentData },
            room_report: { fetching: isRoomFetching, data: roomData },
            country_report: { fetching: isCountryReportFetching, data: countryReportData },
            occupancy_report: { fetching: isOccupancyPropertyMonthlyFetching, data: occupancyPropertyMonthlyData },
        };
        const current = map[reportType];
        if (current && !current.fetching && current.data) {
            toast.success('Group report data loaded successfully.');
        }
    }, [
        reportType,
        isDailyReportFetching, dailyReportData,
        isSalesReportFetching, salesReportData,
        isReportSalesMonthlyFetching, reportSalesMonthlyData,
        isReportReservationFetching, reportReservationData,
        isReportAgentFetching, reportAgentData,
        isRoomFetching, roomData,
        isCountryReportFetching, countryReportData,
        isOccupancyPropertyMonthlyFetching, occupancyPropertyMonthlyData,
    ]);

    const isAnyFetching = isDailyReportFetching || isSalesReportFetching || isReportSalesMonthlyFetching || isReportReservationFetching || isReportAgentFetching || isRoomFetching || isCountryReportFetching || isOccupancyPropertyMonthlyFetching || isOccupancyPropertyMonthlyByMonthFetching || isOccupancyPropertyYearlyFetching || isOccupancyPropertyYearlyByYearFetching;

    return (
        <div className="container-fluid mt-4">
            <HeaderFilter setReportType={setReportType} setFilters={setFilters} isLoading={isAnyFetching} />
            <h3 className="mt-4">
                {reportType === "daily_report" ? "Daily Report" :
                    reportType === "sales_report" ? "Sales Report" :
                        reportType === "report_sales_monthly" ? "Sales Last 12 Months Report" :
                            reportType === "report_reservation" ? "Reservation Last 12 Months Report" :
                                reportType === "report_agent" ? "Agent Report" :
                                    reportType === "room_report" ? "Room Report" :
                                        reportType === "country_report" ? "Country Report" :
                                            reportType === "occupancy_report" ? "Occupancy Report" : "Unknown Report"}
            </h3>
            {isDailyReportFetching && reportType === "daily_report" ? (
                <SkeletonTable rows={10} columns={7} />
            ) : isSalesReportFetching && reportType === "sales_report" ? (
                <SkeletonTable rows={10} columns={9} />
            ) : isReportSalesMonthlyFetching && reportType === 'report_sales_monthly' ? (
                <SkeletonTable rows={10} columns={8} />
            ) : isReportReservationFetching && reportType === 'report_reservation' ? (
                <SkeletonTable rows={10} columns={8} />
            ) : isReportAgentFetching && reportType === 'report_agent' ? (
                <SkeletonTable rows={10} columns={9} />
            ) : isRoomFetching && reportType === 'room_report' ? (
                <SkeletonTable rows={10} columns={10} />
            ) : isCountryReportFetching && reportType === 'country_report' ? ( // <-- 4. Tambah keadaan pemuatan untuk Laporan Negara
                <SkeletonTable rows={10} columns={3} />
            ) : (isOccupancyPropertyMonthlyFetching && isOccupancyPropertyMonthlyByMonthFetching && isOccupancyPropertyYearlyFetching && isOccupancyPropertyYearlyByYearFetching && reportType === 'occupancy_report') ? (
                <SkeletonTable rows={10} columns={3} />
            ) : (
                <>
                    {reportType === "daily_report" && dailyReportData && "data" in dailyReportData && (
                        <DailyReportTableV4
                            data={dailyReportData}
                            orderBy={dailyOrderBy}
                            sort={dailySort}
                            setOrderBy={setDailyOrderBy}
                            setSort={setDailySort}
                        />
                    )}
                    {/* <-- 4. Paparkan komponen V4 untuk Laporan Jualan --> */}
                    {reportType === "sales_report" && salesReportData && "data" in salesReportData && (
                        <SalesReportTableV4
                            data={salesReportData}
                            orderBy={salesOrderBy}
                            sort={salesSort}
                            setOrderBy={setSalesOrderBy}
                            setSort={setSalesSort}
                        />
                    )}
                    {reportType === "report_sales_monthly" && reportSalesMonthlyData && "data" in reportSalesMonthlyData && (
                        <>
                            {/* Komponen Jadual V4 yang baharu menerima keseluruhan objek data */}
                            <ReportSalesMonthlyTableV4 data={reportSalesMonthlyData} />

                            <div className="mt-4">
                                <h3>Sales Last 12 Months Chart</h3>
                                {/* Komponen Carta V4 yang baharu juga menerima keseluruhan objek data */}
                                {/* <ReportSalesMonthlyChartV4 data={reportSalesMonthlyData} /> */}
                                <ReportSalesMonthlyHighchartsV4 data={reportSalesMonthlyData} />
                            </div>
                        </>
                    )}
                    {reportType === 'report_reservation' && reportReservationData && 'data' in reportReservationData && (
                        <>
                            <ReportReservationTableV4 data={reportReservationData} />
                            <div className="mt-4">
                                <h3>Reservation Last 12 Months Chart</h3>
                                {/* <ReportReservationChartV4 data={reportReservationData} /> */}
                                <ReportReservationHighchartsV4 data={reportReservationData} />
                            </div>
                        </>
                    )}
                    {reportType === 'report_agent' && reportAgentData && 'data' in reportAgentData && (
                        <>
                            <ReportAgentTableV4
                                data={reportAgentData}
                                orderBy={agentOrderBy}
                                sort={agentSort}
                                setOrderBy={setAgentOrderBy}
                                setSort={setAgentSort}
                            />
                            <div className="mt-4">
                                <h3>Agent Chart</h3>
                                <div className="d-flex flex-wrap justify-content-center gap-3">
                                    <div className="flex-grow-1 responsive-chart-container">
                                        <GroupAgentHighchartsV4
                                            title="Number Of Room Sold By Source Of: Agent"
                                            data={reportAgentData.data}
                                            dataKey="room_nights"
                                        />
                                    </div>
                                    <div className="flex-grow-1 responsive-chart-container">
                                        <GroupAgentHighchartsV4
                                            title="Total Revenue By Source Of: Agent"
                                            data={reportAgentData.data}
                                            dataKey="revenue"
                                            valueFormatter={formatPrice}
                                        />
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                    {reportType === 'room_report' && roomData && 'data' in roomData && (
                        <>
                            <RoomReportTableV4
                                data={roomData}
                                orderBy={roomOrderBy}
                                sort={roomSort}
                                setOrderBy={setRoomOrderBy}
                                setSort={setRoomSort}
                            />
                            <div className="mt-4">
                                <h3>Room Chart</h3>
                                <div className="d-flex flex-wrap justify-content-center gap-3">
                                    <div className="flex-grow-1 responsive-chart-container">
                                        {/* <GroupRoomEChartV4
                                            title="Number Of Room Sold By Source Of: Property"
                                            data={roomData.data}
                                            dataKey="room_nights"
                                        /> */}
                                        <GroupRoomHighchartsV4
                                            title="Number Of Room Sold By Source Of: Property"
                                            data={roomData.data}
                                            dataKey="room_nights"
                                        />
                                    </div>
                                    <div className="flex-grow-1 responsive-chart-container">
                                        {/* <GroupRoomEChartV4
                                            title="Total Revenue By Source Of: Property"
                                            data={roomData.data}
                                            dataKey="revenue"
                                            valueFormatter={formatPrice}
                                        /> */}
                                        <GroupRoomHighchartsV4
                                            title="Total Revenue By Source Of: Property"
                                            data={roomData.data}
                                            dataKey="revenue"
                                            valueFormatter={formatPrice}
                                        />
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                    {reportType === 'country_report' && countryReportData && 'data' in countryReportData && (
                        <CountryReportTableV4
                            data={countryReportData}
                            orderBy={countryOrderBy}
                            sort={countrySort}
                            setOrderBy={setCountryOrderBy}
                            setSort={setCountrySort}
                        />
                    )}

                    {(reportType === 'occupancy_report' && occupancyPropertyMonthlyData && occupancyPropertyYearlyData && occupancyPropertyMonthlyDataByMonth && occupancyPropertyYearlyDataByYear) && (
                        <OccupancyReportTableV4
                            monthlyData={occupancyPropertyMonthlyData}
                            yearlyData={occupancyPropertyYearlyData}
                            monthlyDataByMonth={occupancyPropertyMonthlyDataByMonth}
                            yearlyDataByYear={occupancyPropertyYearlyDataByYear}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default GroupContent;